import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DynamicDialogTemplate from "../../../components/shared/DynamicDialogTemplate";
import ClassroomForm from "./components/ClassroomForm";
import {
  addStudentsToClassroom,
  createClassroom,
  deleteClassroom,
} from "../../../services/classroomService";
import DbService from "../../../services/DbService";
import { useHandleError } from "../../../hooks/useHandleError";
import { Classroom } from "../../../types/ClassroomModel";
import StudentSelector from "../../../components/StudentSelector";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleIcon from "@mui/icons-material/People";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { EapProgram } from "../../../types/School";
import { PROGRAM_COLORS } from "../../../constants/colors";
import { getPaceDisplay } from "../../../constants/pace";
import EmailAvatar from "../../../components/EmailAvatar";
import SpeedIcon from "@mui/icons-material/Speed";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupsIcon from "@mui/icons-material/Groups";
import EventNoteIcon from "@mui/icons-material/EventNote";

import { useClassroom } from "../../../context/ClassroomContext";
import { enqueueSnackbar } from "notistack";
import { getClassAnalytics } from "../../../services/classroomService";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const ClassroomDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { setClassroom } = useClassroom();

  const handleError = useHandleError();
  const [isStudentDialogOpen, setIsStudentDialogOpen] = useState(false);
  const [selectedClassroom, setSelectedClassroom] = useState<Classroom | null>(
    null
  );

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [atRiskCounts, setAtRiskCounts] = useState<Record<string, number>>({});

  // Calculate summary stats
  const activeTotalClassrooms = classrooms.length;
  const totalStudents = classrooms.reduce(
    (total, classroom) => total + (classroom.students?.length || 0),
    0
  );
  const upcomingSessions = classrooms.filter(
    (classroom) => new Date(classroom.startDate) > new Date()
  ).length;

  useEffect(() => {
    const fetchClassrooms = async () => {
      setLoading(true);
      try {
        const response = await DbService.getClassrooms();
        setClassrooms(response.data);
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchClassrooms();
  }, []);

  // Fetch at-risk student counts per classroom
  useEffect(() => {
    if (classrooms.length === 0) return;
    const fetchAtRisk = async () => {
      const counts: Record<string, number> = {};
      await Promise.all(
        classrooms
          .filter((c) => (c.students?.length || 0) > 0 && c._id)
          .map(async (c) => {
            try {
              const res = await getClassAnalytics(c._id!);
              counts[c._id!] = res.data?.atRiskStudents?.length || 0;
            } catch {
              // silently skip if analytics fails
            }
          })
      );
      setAtRiskCounts(counts);
    };
    fetchAtRisk();
  }, [classrooms]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    classroomId: string
  ) => {
    setOpenMenuId(classroomId);
    setAnchorEl(event.currentTarget); // Set the clicked element as anchor
  };

  const handleMenuClose = (classroomId: string) => {
    setOpenMenuId(null); // Clear the ID
    setAnchorEl(null); // Clear the anchor element
  };

  const handleOpenAddDialog = () => {
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
  };

  // const handleOpenStudentDialog = (classroom: Classroom) => {
  //   setSelectedClassroom(classroom);
  //   setIsStudentDialogOpen(true);
  // };

  // const handleCloseStudentDialog = async (
  //   selectedStudents: { id: string; name: string }[] | undefined
  // ) => {
  //   setIsStudentDialogOpen(false);

  //   if (selectedStudents && selectedClassroom?._id) {
  //     try {
  //       setLoading(true);
  //       await addStudentsToClassroom(selectedClassroom._id, selectedStudents);

  //       setClassrooms((prevClassrooms) =>
  //         prevClassrooms.map((classroom) =>
  //           classroom._id === selectedClassroom._id
  //             ? {
  //                 ...classroom,
  //                 students: [...classroom.students, ...selectedStudents], // Append new students
  //               }
  //             : classroom
  //         )
  //       );
  //     } catch (error) {
  //       handleError(error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   }
  // };

  const handleOpenEditDialog = (classroom: Classroom) => {
    setIsAddDialogOpen(true);
    setSelectedClassroom(classroom);
  };

  const handleDeleteClassroom = async (classroomId: string) => {
    // Handle classroom deletion logic here
    const response = await deleteClassroom(classroomId);
    setClassrooms((prevClassrooms) =>
      prevClassrooms.filter((classroom) => classroom._id !== classroomId)
    );
    handleMenuClose(classroomId); // Close the menu after deletion

    enqueueSnackbar(response.message, {
      variant: "success",
    });

    console.log("Delete classroom:", classroomId);
  };

  const handleSubmit = async (classroom: Classroom) => {
    // Implement submission logic here
    console.log("Classroom submitted:", classroom);
    setLoading(true);
    try {
      let response;
      if (classroom._id) {
        response = await DbService.updateClassroom(classroom._id, classroom);
        setClassrooms((prevClassrooms) =>
          prevClassrooms.map((c) => (c._id === classroom._id ? classroom : c))
        );
      } else {
        response = await DbService.createClassroom(classroom);

        // Important: Use the response data which contains the generated _id from MongoDB
        const newClassroom = response.data;
        setClassrooms((prevClassrooms) => [...prevClassrooms, newClassroom]);
      }
      enqueueSnackbar(response.message, {
        variant: "success",
      });
      setSelectedClassroom(null); // Clear selected classroom after submission
      handleCloseAddDialog();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (input: number | Date): string => {
    const date = input instanceof Date ? input : new Date(input);

    // Format the date with time up to minutes
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    };

    return date.toLocaleDateString("en-AU", options);
  };

  const getBgColor = (program: EapProgram) => {
    if (program.includes("YELLOW")) return PROGRAM_COLORS.EAP_YELLOW;
    if (program.includes("GREEN")) return PROGRAM_COLORS.EAP_GREEN;
    return PROGRAM_COLORS.EAP_ACCESS;
  };

  const getTextColor = (program: EapProgram) => {
    // Get the background color
    const bgColor = getBgColor(program);

    // Calculate relative luminance (simplified version)
    const r = parseInt(bgColor.slice(1, 3), 16) / 255;
    const g = parseInt(bgColor.slice(3, 5), 16) / 255;
    const b = parseInt(bgColor.slice(5, 7), 16) / 255;

    // Weighted luminance formula
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // Use white text on dark backgrounds, black text on light backgrounds
    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  //classrooms wont be able to edit after 24 hours before the course starts
  const isEditable = (
    startDate: number | Date,
    students: { id: string; name: string }[]
  ): boolean => {
    if (!students?.length) {
      return true;
    }

    const classroomStartDate = new Date(startDate);
    const currentDate = new Date();

    // Calculate difference in milliseconds
    const timeDifference = classroomStartDate.getTime() - currentDate.getTime();

    // Convert to hours (1000ms * 60s * 60min = 3600000ms in an hour)
    const hoursDifference = timeDifference / 3600000;

    // Return true if there are at least 24 hours until the start date
    return hoursDifference >= 24;
  };

  const manageStudents = (classroom: Classroom) => {
    setClassroom(classroom);
    navigate(`/teacher/classrooms/${classroom._id}/students`);
  };

  return (
    <>
      {/* Main classroom management section with title and action button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          My Classrooms
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Classroom
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              borderRadius: 2,
              // backgroundColor: theme.palette.background.paper,
              // transition: 'all 0.3s',
              // '&:hover': {
              //   boxShadow: theme.shadows[4],
              //   transform: 'translateY(-2px)'
              // }
            }}
          >
            <Avatar
              sx={{
                // bgcolor: theme.palette.primary.light,
                // color: theme.palette.primary.contrastText,
                mr: 2,
                width: 56,
                height: 56,
              }}
            >
              <DashboardIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: "uppercase", fontWeight: 500 }}
              >
                Active Classrooms
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {activeTotalClassrooms}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              // borderRadius: 2,
              // backgroundColor: theme.palette.background.paper,
              // transition: "all 0.3s",
              // "&:hover": {
              //   boxShadow: theme.shadows[4],
              //   transform: "translateY(-2px)",
              // },
            }}
          >
            <Avatar
              sx={{
                // bgcolor: theme.palette.success.light,
                // color: theme.palette.success.contrastText,
                mr: 2,
                width: 56,
                height: 56,
              }}
            >
              <GroupsIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: "uppercase", fontWeight: 500 }}
              >
                Total Students
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {totalStudents}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              // borderRadius: 2,
              // backgroundColor: theme.palette.background.paper,
              // transition: "all 0.3s",
              // "&:hover": {
              //   boxShadow: theme.shadows[4],
              //   transform: "translateY(-2px)",
              // },
            }}
          >
            <Avatar
              sx={{
                // bgcolor: theme.palette.warning.light,
                // color: theme.palette.warning.contrastText,
                mr: 2,
                width: 56,
                height: 56,
              }}
            >
              <EventNoteIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: "uppercase", fontWeight: 500 }}
              >
                Upcoming Sessions
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {upcomingSessions}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {classrooms?.map((classroom) => (
          <Grid item xs={12} sm={6} md={5} lg={4} key={classroom._id}>
            <Card
              sx={{
                background: "white",
                display: "flex",
                flexDirection: "column", // Ensure content stacks vertically
                justifyContent: "space-between", // Space out content evenly
                height: "100%",
              }}
            >
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {classroom.classroomName}
                    {(atRiskCounts[classroom._id || ""] || 0) > 0 && (
                      <Chip
                        icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                        label={`${atRiskCounts[classroom._id || ""]} at risk`}
                        size="small"
                        color="warning"
                        sx={{ fontSize: ".65rem" }}
                      />
                    )}
                  </Box>
                }
                titleTypographyProps={{
                  variant: "h6",
                }}
                action={
                  isEditable(classroom.startDate, classroom.students) ? (
                    <IconButton
                      onClick={(event) =>
                        classroom._id && handleMenuOpen(event, classroom._id)
                      }
                      sx={{ color: "#000" }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  ) : null
                }
                sx={{
                  marginBottom: "8px",
                  fontWeight: "bold",
                  backgroundColor: getBgColor(classroom.program), // Add background color
                  color: getTextColor(classroom.program), // Optional: Add text color to contrast with background
                  padding: "18px", // Optional: Add some padding
                }}
              />
              <Menu
                anchorEl={anchorEl}
                open={openMenuId === classroom._id}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => handleOpenEditDialog(classroom)}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    classroom._id && handleDeleteClassroom(classroom._id)
                  }
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              </Menu>

              <CardContent sx={{ paddingBottom: "16px" }}>
                <Grid container spacing={3}>
                  {/* Start Date */}
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textTransform: "uppercase",
                        fontWeight: 500,
                        display: "block",
                      }}
                    >
                      START DATE
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(classroom.startDate)}
                    </Typography>
                  </Grid>

                  {/* End Date */}
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textTransform: "uppercase",
                        fontWeight: 500,
                        display: "block",
                      }}
                    >
                      END DATE
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(classroom.endDate)}
                    </Typography>
                  </Grid>
                </Grid>
                {/* Pace */}
                <Grid item xs={12} sx={{ margin: "1rem 0" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textTransform: "uppercase",
                      fontWeight: 500,
                      display: "block",
                    }}
                  >
                    PACE
                  </Typography>
                  <Typography variant="body1">
                    {getPaceDisplay(classroom.pace)}
                  </Typography>
                </Grid>
                <Divider sx={{ margin: "1rem 0" }} />
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PeopleIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textTransform: "uppercase",
                        fontWeight: 500,
                      }}
                    >
                      STUDENTS
                    </Typography>
                  </Box>

                  {classroom.students?.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 1,
                        alignItems: "center",
                      }}
                    >
                      {classroom.students.slice(0, 2).map((s) => (
                        <EmailAvatar key={s.id} email={s.name} size={32} />
                      ))}

                      <Typography variant="body1" sx={{ ml: 1 }}>
                        {classroom.students.length > 2 &&
                          ` +${classroom.students.length - 2} more`}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{
                        mt: 0.5,
                        fontStyle: "italic",
                        color: "text.secondary",
                        fontWeight: "10",
                      }}
                    >
                      No students
                    </Typography>
                  )}
                </Grid>
                <Divider sx={{ margin: "1rem 0" }} />
                {/* <Button
                          color="primary"
                          variant="contained"
                          onClick={() => best way to open the student management page}
                        >
                          Add Students
                        </Button> */}
              </CardContent>
              <Box sx={{ padding: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => manageStudents(classroom)}
                  sx={{
                    mt: { xs: 2, md: 0 },
                  }}
                >
                  Manage Students
                </Button>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    setClassroom(classroom);
                    navigate(`/teacher/classrooms/${classroom._id}/progress`);
                  }}
                >
                  View Progress
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setClassroom(classroom);
                    navigate(`/teacher/classrooms/${classroom._id}/quiz-results`);
                  }}
                >
                  Quiz Results
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setClassroom(classroom);
                    navigate(`/teacher/classrooms/${classroom._id}/analytics`);
                  }}
                >
                  Analytics
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <DynamicDialogTemplate
        isOpen={isAddDialogOpen}
        title="Classroom"
        onClose={handleCloseAddDialog}
      >
        {/* Render ClassroomForm with necessary props */}
        <ClassroomForm
          selectedRow={selectedClassroom || undefined}
          onSubmit={handleSubmit}
        />
      </DynamicDialogTemplate>

      {/* <DynamicDialogTemplate
        isOpen={isStudentDialogOpen}
        title="Select Students"
        onClose={() => handleCloseStudentDialog(undefined)}
      >
        {selectedClassroom && (
          <StudentSelector
            classroomProgram={selectedClassroom.program}
            onClose={handleCloseStudentDialog}
          />
        )}
      </DynamicDialogTemplate> */}
    </>
  );
};

export default ClassroomDashboard;
